import classnames from 'classnames';
import * as React from 'react';

import { Icon } from '@ao-internal/components-react';
import { useWindowScroll } from '@react-hook/window-scroll';
import useTimeout from '@rooks/use-timeout';

interface ToastNotificationProps extends React.HTMLProps<HTMLDivElement> {
	scrollToPosY?: number;
	waitTime?: number;
	onOpen?: () => void;
	onClose?: () => void;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
	waitTime = 0,
	scrollToPosY = 0,
	onOpen,
	onClose,
	children,
	className,
	...props
}) => {
	const [showNotification, setShowNotification] = React.useState(false);
	const [fps, setFps] = React.useState(30);
	const [waitedLongEnough, setWaitedLongEnough] = React.useState(false);
	const scrollY = useWindowScroll(fps);
	const { start: startTimer, clear: clearTimer } = useTimeout(() => {
		setWaitedLongEnough(true);
	}, waitTime);

	function destroy() {
		setFps(0); // Stop the scroll event listener
		setShowNotification(false);
		clearTimer();
	}

	function handleDismissButtonClick() {
		if (onClose) {
			onClose();
		}

		destroy();
	}

	React.useEffect(() => {
		startTimer();
		return () => {
			setFps(0); // Stop the scroll event listener
			clearTimer();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (scrollY >= scrollToPosY && waitedLongEnough) {
			setFps(0); // Stop the scroll event listener
			setShowNotification(true);
		}
	}, [scrollY, waitedLongEnough, scrollToPosY]);

	React.useEffect(() => {
		if (showNotification && onOpen) {
			onOpen();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showNotification]);

	return (
		<div
			className={classnames(
				'c-chat-toast-notification c-chat-toast-notification-lozenge left-0 right-auto ml-4 md:left-auto md:right-0 md:ml-0 md:mr-5',
				className,
				{ 'c-chat-toast-notification--open': showNotification }
			)}
			{...props}
		>
			<div className="c-chat-toast-notification__message md:rounded-md md:rounded-tr-none text-body w-full p-4 bg-white rounded-md rounded-tl-none">
				{children}
			</div>
			<button
				className="c-chat-toast-notification__dismiss-cta md:left-auto md:right-0 absolute left-0 flex items-center justify-center bg-white rounded-full"
				data-tag-name="dismiss"
				data-tag-action="close"
				type="button"
				onClick={() => handleDismissButtonClick()}
			>
				<span className="sr-only">Dismiss</span>
				<Icon icon="close" size="lg" className="text-cta-action" />
			</button>
		</div>
	);
};

export default ToastNotification;
